import React, {useEffect, useMemo, useState, useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import {KTSVG} from '../../../../_metronic/helpers'
import {ENV} from '../../../Utils/_helpers/EnvHelper'
import {QRCode} from 'react-qrcode-logo'
import './WalletCardList.scss'
import ReactDOM from 'react-dom'
import moment from 'moment-timezone'
import NodataYet from '../../../Components/NodataYet'
import ReactPaginate from 'react-paginate'
import {toast} from 'react-toastify'
import {WalletCardMobileCard} from './components'
import {
  STATUS,
  VALUE_INACTIVE,
  VALUE_CARD,
  VALUE_CARD_UPPER,
  TAB_LIST,
  TOTAL_SCAN,
} from '../../../Utils/Constant/WalletCard'
import {
  useDeleteWalletCardMutation,
  useGetAccessToProductQuery,
  useGetProductQuotaQuery,
  useGetSubscriptionQuery,
  useLazyGetWalletCardListQuery,
  useGetProductQuery,
} from '../../../../services/cellcast.v2'
import {ApiWalletCardsResponse, CardItem} from './type'
import {useI18n} from '../../../Utils/_helpers/useI18n'
import {defaultPage, defaultPageSize, pageSizeOptions} from '../../../Utils/Constant/WalletCard'
import DeleteConfirmationPopup from '../../../Components/DeleteConfirmationPopup'
import {useTimezone} from '../../../hooks/useTimezone'
import {useDispatch} from 'react-redux'
import {startLoadding, stopLoading} from '../../../Redux/FrontEnd/Loading/LoadingSlice'
import {MenuComponent} from '../../../../_metronic/assets/ts/components'
import FeatureSplashModal from '../../../Components/FeatureSplashModal'
import QuotaLimitModal from '../../../Components/QuotaLimitModal'
import {useGetProductPlanQuery} from '../../../../services/cellcast.v2'
import BcardSplashScreen from './BcardSplashScreen'
import { formatDateWithUserTimezone } from '../wallet-card-templates/utils'
import { useResponsive } from '../../../hooks/useResponsive'

const WalletCardList = () => {
  const navigate = useNavigate()
  const {t} = useI18n()
  const {userTimezone} = useTimezone()

  const [getWalletCardList, {isLoading}] = useLazyGetWalletCardListQuery()
  const [deleteWalletCard] = useDeleteWalletCardMutation()
  const {data: productData} = useGetProductQuery({productKey: 'bcard'})
  const productId = productData?.data?.product?._id || undefined
  const {data: accessToProduct, refetch: refetchAccessToProduct} = useGetAccessToProductQuery(
    {productId: productId as string},
    {skip: !productId}
  )

  const {data: productPlansResponse, refetch: refetchProductPlans} = useGetProductPlanQuery(
    {productId: productId as string},
    {skip: !productId}
  )
  const productPlans = productPlansResponse?.data?.plans || []
  const product = productData?.data?.product || undefined
  const {data: productQuotaResponse, refetch: refetchProductQuota} = useGetProductQuotaQuery(
    {productId: productId as string},
    {skip: !productId}
  )

  const {data: subscriptionData} = useGetSubscriptionQuery(
    {productId: productId as string},
    {skip: !productId}
  )

  const currentPlan = subscriptionData?.data?.subscriptions[0]?.planId
  const productQuota = productQuotaResponse?.data?.quota_usage

  const dataAccessToProduct = accessToProduct?.data

  const [open, setOpen] = useState(false)
  const [showSplash, setShowSplash] = useState(false)
  const [showQuotaLimit, setShowQuotaLimit] = useState(false)
  const [deleteIds, setDeleteIds] = useState<string | null>(null)
  const [hasApiError, setHasApiError] = useState(false)
  const dispatch = useDispatch()

  // Error state for API calls
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | null>(null)
  const [templates, setTemplates] = useState<ApiWalletCardsResponse>({
    items: [],
    total: 0,
    totalPages: 0,
    current: 1,
  })

  const [page, setPage] = useState({
    page: defaultPage,
    size: defaultPageSize,
  })

  const updateTemplates = useCallback((updatedData: ApiWalletCardsResponse) => {
    setTemplates(updatedData)
  }, [])

  const fetchData = useCallback(async () => {
    if (isLoading || hasApiError) return

    dispatch(startLoadding())
    setError(null)

    try {
      const response: any = await getWalletCardList({page: page.page, limit: page.size})
      if (response.data) {
        updateTemplates(response.data)
        setHasApiError(false)
      } else if (response.error) {
        throw new Error(response.error.message || 'Failed to fetch data')
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch data')
      toast.error(err?.message || 'Failed to fetch data')
      setHasApiError(true)
    } finally {
      dispatch(stopLoading())
    }
  }, [page, getWalletCardList, isLoading, hasApiError, dispatch, updateTemplates])

  // Used in error handling scenarios - can be triggered from UI if needed
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const retryFetchData = () => {
    setHasApiError(false)
    fetchData()
  }

  const handleConfirmDelete = async () => {
    try {
      dispatch(startLoadding())
      const res: any = await deleteWalletCard({id: deleteIds!})
      if (res.data.status) {
        toast.success(res.data.message)
        if (templates.items.length === 1 && page.page > 1) {
          setPage((prev) => ({...prev, page: prev.page - 1}))
        } else {
          fetchData()
        }

        // Immediately refetch quota data after successful deletion
        await refetchProductQuota()
      } else {
        toast.error(res.data.message)
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete template')
    } finally {
      dispatch(stopLoading())
      setOpen(false)
      setDeleteIds(null)
    }
  }

  const handleDelete = useCallback((data: CardItem) => {
    setDeleteIds(data.id)
    setOpen(true)
  }, [])

  const { isMobile } = useResponsive()


  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Refetch API data when component mounts or when modal closes
  useEffect(() => {
    const refetchAllData = async () => {
      try {
        await Promise.all([refetchAccessToProduct(), refetchProductPlans(), refetchProductQuota()])
      } catch (error) {
        console.error('Failed to refetch data:', error)
      }
    }

    refetchAllData()
  }, [refetchAccessToProduct, refetchProductPlans, refetchProductQuota])

  // Add this new useEffect to reinitialize the menu when component mounts or is updated
  useEffect(() => {
    // Wait for DOM to be fully rendered
    const timer = setTimeout(() => {
      // Reinitialize all menus in the component
      MenuComponent.createInstances('[data-kt-menu="true"]')

      // Force update dropdown menus
      MenuComponent.updateDropdowns()
    }, 200)

    return () => {
      clearTimeout(timer)
    }
  }, [templates.items]) // Reinitialize when the list items change

  // Add this new useEffect
  useEffect(() => {
    // Force Metronic menu initialization
    setTimeout(() => {
      MenuComponent.reinitialization()

      // Look for any wallet-card specific menus and initialize them
      const dropdownMenus = document.querySelectorAll('.menu-action-wallet-card')
      if (dropdownMenus.length > 0) {
        dropdownMenus.forEach((menu) => {
          if (menu instanceof HTMLElement) {
            menu.setAttribute('data-kt-menu', 'true')
          }
        })

        // Reinitialize again to catch newly marked elements
        MenuComponent.reinitialization()
      }
    }, 300)
  }, [templates.items]) // Reinitialize when the items change

  const handlePageChange = useCallback((selectedPage: number) => {
    setHasApiError(false)
    setPage((prev) => ({...prev, page: selectedPage + 1}))
  }, [])

  const handleEdit = useCallback(
    (data: CardItem) => {
      navigate(`/wallet-card/edit/${data.id}`, {state: {isEdit: true}})
    },
    [navigate]
  )

  const handleDetail = useCallback(
    (data: CardItem) => {
      navigate(`/wallet-card/detail/${data.id}`)
    },
    [navigate]
  )
  const handleCreateNew = async () => {
    // Refetch quota data to ensure we have the latest values
    await refetchProductQuota()

    if (dataAccessToProduct && dataAccessToProduct.hasAccess === false) {
      // If user doesn't have access to the product, show splash modal
      setShowSplash(true)
    } else if (
      productQuota &&
      !productQuota.isUnlimited &&
      productQuota.currentUsage >= productQuota.quotaLimit
    ) {
      // If user has reached quota limit, show quota limit modal
      setShowQuotaLimit(true)
    } else {
      // If user has access and hasn't reached quota limit, navigate to create page
      navigate('/wallet-card/create', {state: {isEdit: false}})
    }
  }

  const handleDownloadQR = useCallback(
    (data: CardItem) => {
      dispatch(startLoadding())
      const qrCodeUrl = `${ENV().REACT_APP_FE_URL}/wallet-card/public/${data.id}`
      const tempDiv = document.createElement('div')
      document.body.appendChild(tempDiv)

      const qrElement = (
        <QRCode
          value={qrCodeUrl}
          size={250}
          fgColor='#000000'
          bgColor='#ffffff'
          qrStyle='dots'
          id={`qr-code-${data.id}`}
          eyeRadius={5}
        />
      )

      ReactDOM.render(qrElement, tempDiv)

      setTimeout(() => {
        const canvas = tempDiv.querySelector('canvas') as HTMLCanvasElement
        if (canvas) {
          const dataURL = canvas.toDataURL('image/png')

          const link = document.createElement('a')
          const formattedDate = moment().format('YYYYMMDD')
          link.href = dataURL
          link.download = `${data.cardName}-${formattedDate}.png`
          link.click()
        }
        ReactDOM.unmountComponentAtNode(tempDiv)
        document.body.removeChild(tempDiv)
        dispatch(stopLoading())
      }, 500)
    },
    [dispatch]
  )

  const renderActions = useCallback(
    (data: any, idx: number, total: number) => {
      const commonStyle = {verticalAlign: 'center'}
      const isNearBottom = idx >= total - 2

      return (
        <td key={`${data.id}-action`} className='ms-2 text-end' style={commonStyle}>
          <div className='dropdown-hover-show'>
            <button
              className='btn btn-sm btn-icon btn-bg-light btn-active-light-primary d-flex align-items-center justify-content-center'
              data-kt-menu-trigger='hover'
              data-kt-menu-placement={isNearBottom ? 'top-end' : 'bottom-end'}
              data-kt-menu-overflow='false'
            >
              <span className='more-action-icon'>...</span>
            </button>

            <div
              className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-state-bg-light-primary fw-bold fs-7 w-200px py-4'
              data-kt-menu='true'
            >
              <div className='menu-item px-3' onClick={() => handleEdit(data)}>
                <span className='menu-link px-3 d-flex align-items-center'>
                  <span className='svg-icon svg-icon-2 me-3'>
                    <i className='bi bi-pencil me-2'></i>
                  </span>
                  <span>{t('COMMON.ACTIONS.EDIT')}</span>
                </span>
              </div>

              <div className='menu-item px-3' onClick={() => handleDetail(data)}>
                <span className='menu-link px-3 d-flex align-items-center'>
                  <span className='svg-icon svg-icon-2 me-3'>
                    <i className='bi bi-eye me-2'></i>
                  </span>
                  <span>{t('WALLET_CARD.ACTIVE.VIEW_CARD')}</span>
                </span>
              </div>

              <div className='menu-item px-3' onClick={() => handleDelete(data)}>
                <span className='menu-link px-3 d-flex align-items-center text-danger'>
                  <span className='svg-icon svg-icon-danger svg-icon-2 me-3'>
                    <i className='bi bi-trash me-2 text-danger'></i>
                  </span>
                  <span>{t('COMMON.ACTIONS.DELETE')}</span>
                </span>
              </div>
            </div>
          </div>
        </td>
      )
    },
    [handleEdit, handleDetail, handleDelete, t]
  )

  const renderCell = useCallback(
    (key: string, data: CardItem, idx: number) => {
      const commonStyle = {verticalAlign: 'middle'}

      switch (key) {
        case 'imagePreviewUrl':
          return (
            <td key={`${data.id}-${key}`} style={commonStyle} className='w-100px'>
              <img src={data.imagePreviewUrl} alt='Preview' className='img-preview w-80px' />
            </td>
          )
        case 'type':
          return (
            <td key={`${data.id}-${key}`} className='ps-3 icon-business-card'>
              <KTSVG
                className='svg-icon-business-card'
                path='/media/icons/wallet-card/business-card.svg'
              />
            </td>
          )
        case 'cardType':
          return (
            <td key={`${data.id}-${key}`} style={commonStyle}>
              <span className={`text-dark d-block mb-1 fs-6 card-type ${data.type}`}>
                {data.type
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/^./, (str) => str.toUpperCase())
                  .trim()
                  .replace(VALUE_CARD, VALUE_CARD_UPPER)}
              </span>
            </td>
          )
        case 'cardName':
          return (
            <td key={`${data.id}-${key}`} style={commonStyle}>
              <div>
                <div className='d-flex align-items-center'>
                  <span
                    className='text-dark d-block mb-1 fs-6 wallet-card-name cursor-pointer'
                    onClick={() => handleDetail(data)}
                  >
                    {data.cardName}
                  </span>
                </div>
              </div>
            </td>
          )
        case 'createdAt':
          return (
            <td key={`${data.id}-${key}`} style={commonStyle}>
              <span className='text-dark fw-400 d-block mb-1 fs-6 created-at'>
                {formatDateWithUserTimezone(data.createdAt || '', 'MMMM DD, YYYY hh:mm A')}
              </span>
            </td>
          )
        case 'updatedAt':
          return (
            <td key={`${data.id}-${key}`} style={commonStyle}>
              <span className='text-dark fw-400 d-block mb-1 fs-6 updated-at'>
                {formatDateWithUserTimezone(data.updatedAt || '', 'MMMM DD, YYYY hh:mm A')}
              </span>
            </td>
          )
        case 'status':
          return (
            <td key={`${data.id}-${key}`} style={commonStyle}>
              <span
                className={`badge ${data.status === STATUS.ACTIVE ? 'bg-success' : 'bg-danger'}`}
              >
                {data.status === STATUS.INACTIVE
                  ? VALUE_INACTIVE
                  : data.status?.charAt(0).toUpperCase() + data.status?.slice(1)}
              </span>
            </td>
          )
        case 'action':
          return renderActions(data, idx, templates.items.length)
        default:
          return (
            <td
              key={`${data.id}-${key}`}
              style={commonStyle}
              className={`${key === TOTAL_SCAN ? 'text-center' : ''}`}
            >
              {data[key] || '-'}
            </td>
          )
      }
    },
    [templates.items.length, formatDateWithUserTimezone, handleDetail, renderActions]
  )

  const renderMobileCards = useMemo(() => {
    if (!templates.items || templates.items.length === 0) {
      return <NodataYet message={t('WALLET_CARD.NO_DATA_WALLET_CARD_LIST')} />
    }

    return (
      <>
        <div className='mobile-cards-container'>
          {templates.items.map((card) => (
            <WalletCardMobileCard
              key={card.id}
              card={card}
              onEdit={handleEdit}
              onView={handleDetail}
              onDelete={handleDelete}
            />
          ))}
        </div>

        {/* Mobile Pagination */}
        <div className='mobile-pagination-container mt-4'>
          <div className='d-flex align-items-center justify-content-between flex-wrap gap-3'>
            <div className='total-cards'>
              <h4 className='text-muted m-0 fs-6'>
                Total Cards: <strong>{templates?.total || 0}</strong>
              </h4>
            </div>
            <select
              value={page?.size}
              className='form-select form-select-sm'
              style={{width: 'auto', minWidth: '80px'}}
              onChange={(e: any) => {
                setHasApiError(false)
                setPage({...page, size: e.target.value, page: 1})
              }}
            >
              {pageSizeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              {templates?.total > 0 &&
                templates?.total > pageSizeOptions[pageSizeOptions.length - 1].value && (
                  <option value={templates?.total}>All</option>
                )}
            </select>
          </div>

          <div className='d-flex justify-content-center mt-3'>
            <ReactPaginate
              previousLabel='<'
              nextLabel='>'
              pageCount={templates?.totalPages}
              forcePage={templates.current - 1}
              onPageChange={(e) => handlePageChange(e.selected)}
              containerClassName={'pagination pagination-sm'}
              pageLinkClassName={'page-link'}
              previousLinkClassName={'page-link'}
              nextLinkClassName={'page-link'}
              disabledClassName={'page-link disabled'}
              activeClassName={'page-item active'}
              breakLabel={'...'}
              breakClassName={'page-item'}
              breakLinkClassName={'page-link'}
            />
          </div>
        </div>
      </>
    )
  }, [
    templates,
    page,
    handleEdit,
    handleDetail,
    handleDelete,
    handleDownloadQR,
    handlePageChange,
    t,
  ])

  const renderTable = useMemo(() => {
    if (!templates.items || templates.items.length === 0) {
      return <NodataYet message={t('WALLET_CARD.NO_DATA_WALLET_CARD_LIST')} />
    }

    return (
      <>
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-400 text-muted'>
                {TAB_LIST.map((tab: {key: string; label: string}) => (
                  <th
                    key={tab.key}
                    className={tab.key === 'action' ? 'min-w-150px text-end' : 'min-w-100px'}
                  >
                    {tab.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {templates.items.map((data, idx) => (
                <tr key={data.id}>{TAB_LIST.map((tab) => renderCell(tab.key, data, idx))}</tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className='abcd'>
          <div className='d-flex align-items-center'>
            <div className='total-cards me-4'>
              <h4 className='text-muted m-0'>
                Total Cards: <strong>{templates?.total || 0}</strong>
              </h4>
            </div>
            <select
              value={page?.size}
              className='SelectSize'
              onChange={(e: any) => {
                setHasApiError(false)
                setPage({...page, size: e.target.value, page: 1})
              }}
            >
              {pageSizeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              {templates?.total > 0 &&
                templates?.total > pageSizeOptions[pageSizeOptions.length - 1].value && (
                  <option value={templates?.total}>All</option>
                )}
            </select>
          </div>
          <ReactPaginate
            previousLabel='<'
            nextLabel='>'
            pageCount={templates?.totalPages}
            forcePage={templates.current - 1}
            onPageChange={(e) => handlePageChange(e.selected)}
            containerClassName={'pagiantion'}
            previousLinkClassName={'pagination__link'}
            nextLinkClassName={'pagination__link'}
            disabledClassName={'pagination__link--disabled'}
            activeClassName={'pagination__link--active'}
          />
        </div>
      </>
    )
  }, [templates, page, renderCell, handlePageChange, t])

  return (
    <>
      <FeatureSplashModal
        show={showSplash}
        onClose={() => setShowSplash(false)}
        onActivate={() => {
          setShowSplash(false)
        }}
        product={product}
        isClosable={true}
        description={<BcardSplashScreen />}
      />

      <QuotaLimitModal
        show={showQuotaLimit}
        onClose={() => {
          setShowQuotaLimit(false)
          refetchProductQuota()
        }}
        onUpgrade={() => {
          setShowQuotaLimit(false)
        }}
        plans={productPlans}
        currentUsage={productQuota?.currentUsage}
        currentPlan={productPlans.find((plan) => plan.planKey === currentPlan?.planKey)}
      />
      <div className='card wallet-card-list'>
        <div className='card-header border-0 pt-12 px-14'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-2'>{t('MOBILE_WALLET_CARD.LIST.TITLE')}</span>
            <span className='text-muted fw-bold fs-7'>{t('COMMON.ORDERED_BY_MOST_RECENT')}</span>
          </h3>
          <div className='card-toolbar'>
            <div className='d-flex card-toolbar'>
              <button className='btn btn-primary p-4' onClick={handleCreateNew}>
                {t('WALLET_CARD.BUTTON.CREATE')}
              </button>
            </div>
          </div>
        </div>

        <div className='card-body py-3 px-14'>{isMobile ? renderMobileCards : renderTable}</div>
      </div>
      <DeleteConfirmationPopup
        show={open}
        handleClose={() => {
          setOpen(false)
          setDeleteIds(null)
        }}
        confirm={handleConfirmDelete}
        Message={t('COMMON.ACTIONS.DELETE_CONFIRMATION', {name: 'wallet card'})}
      />
    </>
  )
}

export default WalletCardList
